import {
  Track,
  Powtoon,
  ManifestVideoAvatarAsset,
  ManifestAssetSound,
  ManifestUserProviderSound,
  ManifestUserSoundBase,
  Manifest
} from 'powtoon-versioning/src/currentSchema'
import {Powtoon as PowtoonService} from 'powtoon-services/powtoons'
import {getSpriteOrSpriteContent} from './trackSpriteUtils'
import {
  getLipSyncAudioTrackFromSprite,
  isActorItem,
  isAvatarVideoItem
} from '../index'
import {getManifestItemForDocItem} from '../manifest'
import getPowtoonTracks from './getPowtoonTracks'
import {isEmpty} from 'lodash'

export enum PowtoonExportReadinessStatus {
  READY = 'Ready',
  DATA_MISSING = 'DataMissing',
  REQUIRE_APPROVAL = 'RequireApproval'
}

interface PowtoonExportReadinessDetails {
  dataMissingItems: Track[],
  requireApprovalItems: Track[]
}

export type PowtoonExportReadinessState = {
  status: PowtoonExportReadinessStatus.READY
} | {
  status: PowtoonExportReadinessStatus.DATA_MISSING | PowtoonExportReadinessStatus.REQUIRE_APPROVAL,
  details: PowtoonExportReadinessDetails
}

export const POWTOON_EXPORT_READINESS_STATE_READY: PowtoonExportReadinessState =
  {status: PowtoonExportReadinessStatus.READY}

const isNotReadinessTrack = (track: Track) => isActorItem(getSpriteOrSpriteContent(track))
const isDataMissingTrack = (track: Track) => !getLipSyncAudioTrackFromSprite(getSpriteOrSpriteContent(track))

export const getNotReadyContentSummary = (powtoon: Powtoon): PowtoonExportReadinessDetails => {

  const result: PowtoonExportReadinessDetails = {dataMissingItems: [], requireApprovalItems: []}
  const allActorItem = getPowtoonTracks<Track>(powtoon, isNotReadinessTrack)

  if (isEmpty(allActorItem)) {
    return result
  }

  return allActorItem.reduce<PowtoonExportReadinessDetails>(
    ({dataMissingItems, requireApprovalItems}, track) => (
      isDataMissingTrack(track) ?
        {
          requireApprovalItems,
          dataMissingItems: [...dataMissingItems, track]
        } :
        {
          requireApprovalItems: [...requireApprovalItems, track],
          dataMissingItems
        }
    ), result
  )
}

const isAvatarVideoTrack = (track: Track) => isAvatarVideoItem(getSpriteOrSpriteContent(track))
const isActorTrack = (track: Track) => isActorItem(getSpriteOrSpriteContent(track))

export const getProcessingAvatarsTotalDuration = (powtoon: Powtoon): number => {
  const result = 0
  if (!powtoon?.manifest) {
    return result
  }
  const allAvatarVideoItem = getPowtoonTracks<Track>(powtoon, isAvatarVideoTrack)

  if (isEmpty(allAvatarVideoItem)) {
    return result
  }

  const manifestObject = powtoon.manifest
  return Math.ceil(allAvatarVideoItem.reduce<number>((acc, track) => {
    const item = getSpriteOrSpriteContent(track)
    const videoAvatarAsset= getManifestItemForDocItem(manifestObject, item) as ManifestVideoAvatarAsset

    if (videoAvatarAsset?.status === 'Processing') {
      const audioTrack = getLipSyncAudioTrackFromSprite(item)
      const manifestAssetSound = getManifestItemForDocItem(
        manifestObject,
        audioTrack
      ) as ManifestAssetSound | ManifestUserProviderSound | ManifestUserSoundBase
      return acc += Math.ceil(manifestAssetSound?.length) || 0
    }
    return acc
  }, result))
}

export const getUngeneratedAvatarsDurations = (powtoon: Powtoon): number[] => {
  const allActorItem = getPowtoonTracks<Track>(powtoon, isActorTrack)
  return getActorTracksDurations(allActorItem, powtoon.manifest)
}

export const getActorTracksDurations = (actorTrack: Track[], manifestObject?: Manifest): number[] => {
  if (!manifestObject || isEmpty(actorTrack)) {
    return []
  }

  return actorTrack.reduce<number[]>((acc, track) => {
    const audioTrack = getLipSyncAudioTrackFromSprite(getSpriteOrSpriteContent(track))
    if (audioTrack) {
      const manifestAssetSound = getManifestItemForDocItem(
        manifestObject,
        audioTrack
      ) as ManifestAssetSound | ManifestUserProviderSound | ManifestUserSoundBase
      return manifestAssetSound?.length ? [...acc, Math.ceil(manifestAssetSound?.length)] : acc
    }
    return acc
  }, [])
}

export const getPowtoonExportReadinessState = (
  powtoon?: Powtoon
): PowtoonExportReadinessState => {
  if (!powtoon) {
    throw new Error('getPowtoonExportReadinessState requires powtoon to process')
  }
  const notReadyContentSummary = getNotReadyContentSummary(powtoon)
  if (!isEmpty(notReadyContentSummary.dataMissingItems)) {
    return {
      status: PowtoonExportReadinessStatus.DATA_MISSING,
      details: notReadyContentSummary
    }
  }
  else if (!isEmpty(notReadyContentSummary.requireApprovalItems)) {
    return {
      status: PowtoonExportReadinessStatus.REQUIRE_APPROVAL,
      details: notReadyContentSummary
    }
  }
  return POWTOON_EXPORT_READINESS_STATE_READY
}

export const getPowtoonExportReadiness = (
  powtoon: PowtoonService
): PowtoonExportReadinessStatus | undefined => {
  return powtoon?.exportReadiness
}

export default getPowtoonExportReadinessState